import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Mail, Phone, MapPin, Pencil, Upload, FileText, Plus, Download, Trash2,
  ShieldCheck, Lock, History, ListChecks, CheckSquare, Square,
} from "lucide-react";
import { api, initials, avatarColor, STATUS_META, shortDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Donut } from "@/components/Donut";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import EmployeeForm from "@/components/EmployeeForm";
import { RecordDialog, RECORD_TYPES } from "@/pages/Reviews";
import { toast } from "sonner";

const RECORD_LABEL = Object.fromEntries(RECORD_TYPES.map((r) => [r.v, r.l]));
const LEVELS = [
  { v: "not_assessed", l: "Not assessed", cls: "bg-slate-100 text-slate-500" },
  { v: "developing", l: "Developing", cls: "bg-amber-50 text-amber-700" },
  { v: "competent", l: "Competent", cls: "bg-sky-50 text-sky-700" },
  { v: "expert", l: "Expert", cls: "bg-emerald-50 text-emerald-700" },
];

const TABS = ["overview", "services", "training", "performance", "documents", "development"];
const CAT_FOR_TAB = { training: "training", performance: "performance", development: "development", documents: "document" };

export default function EmployeeProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [emp, setEmp] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const load = useCallback(() => api.get(`/employees/${id}`).then((r) => setEmp(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  if (!emp) return <div className="text-slate-500">Loading…</div>;
  const m = STATUS_META[emp.employment_status] || STATUS_META.other;

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/people")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
        <ArrowLeft className="h-4 w-4" /> Back to People
      </button>

      {/* Header */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-4">
            <div className={`flex h-16 w-16 items-center justify-center rounded-2xl text-xl font-bold ${avatarColor(emp.full_name)}`}>
              {initials(emp.full_name)}
            </div>
            <div>
              <h1 className="font-heading text-2xl font-extrabold text-slate-900">{emp.full_name}</h1>
              <p className="text-slate-500">{emp.job_role}</p>
              <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
                {emp.region_name && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-slate-600">{emp.region_name}</span>}
                <span className={`rounded-full border px-2 py-0.5 font-medium ${m.cls}`}>{m.label}</span>
                {emp.gdpr_anonymised && (
                  <span data-testid="anonymised-badge" className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 font-medium text-slate-600">
                    <Lock className="h-3 w-3" /> Anonymised
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Donut value={emp.elearning_percentage} size={72} stroke={9} />
            {emp.can_manage && (
              <Button data-testid="edit-employee-button" onClick={() => setEditOpen(true)} variant="outline">
                <Pencil className="mr-1.5 h-4 w-4" /> Edit
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="overview">
        <TabsList data-testid="profile-tabs" className="flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-100 p-1">
          {TABS.map((t) => (
            <TabsTrigger key={t} value={t} data-testid={`profile-tab-${t}`} className="flex-1 capitalize data-[state=active]:bg-white data-[state=active]:text-orange-600">
              {t}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview" className="mt-5">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm lg:col-span-2">
              <h3 className="mb-4 font-heading font-bold text-slate-900">Employee details</h3>
              <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Detail label="Full name" value={emp.full_name} />
                <Detail label="Role" value={emp.job_role} />
                <Detail label="Region" value={emp.region_name} />
                <Detail label="RCSM (manager)" value={emp.rcsm_name} />
                <Detail label="CNAT(s)" value={emp.cnats?.map((c) => c.name).join(", ") || "—"} />
                <Detail label="Employment status" value={m.label} />
                <Detail label="eLeaP training" value={`${emp.elearning_percentage}%`} />
                <Detail label="Start date" value={shortDate(emp.start_date)} />
                <Detail label="Next review" value={shortDate(emp.next_review_date)} />
                <Detail label="Supervisions" value={emp.supervision_count} />
              </dl>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
              <h3 className="mb-4 font-heading font-bold text-slate-900">Contact</h3>
              <ul className="space-y-3 text-sm">
                <li className="flex items-center gap-2 text-slate-600"><Mail className="h-4 w-4 text-slate-400" /> {emp.work_email || "—"}</li>
                <li className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /> {emp.work_phone || "—"} <span className="text-xs text-slate-400">(work)</span></li>
                <li className="flex items-center gap-2 text-slate-600"><Phone className="h-4 w-4 text-slate-400" /> {emp.personal_phone || "—"} <span className="text-xs text-slate-400">(personal)</span></li>
                <li className="flex items-start gap-2 text-slate-600"><MapPin className="mt-0.5 h-4 w-4 text-slate-400" /> {emp.address || "—"}{emp.postcode ? `, ${emp.postcode}` : ""}</li>
              </ul>
            </div>
          </div>
          <div className="mt-6">
            <OnboardingChecklist employee={emp} canManage={emp.can_manage} />
          </div>
          {(user?.is_super || user?.role === "admin" || emp.can_manage) && (
            <div className="mt-6">
              <GdprPanel employee={emp} user={user} onChanged={load} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="services" className="mt-5 space-y-6">
          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <h3 className="mb-4 font-heading font-bold text-slate-900">Regional responsibility & clinical allocation</h3>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <Detail label="Primary region" value={emp.region_name} />
              <Detail label="Managing RCSM" value={emp.rcsm_name} />
              <Detail label="Assigned CNAT(s)" value={emp.cnats?.map((c) => c.name).join(", ") || "—"} />
              <Detail label="Postcode" value={emp.postcode} />
            </div>
          </div>
          <SkillsMatrix employee={emp} canManage={emp.can_manage} />
        </TabsContent>

        <DocTab tab="training" employee={emp} title="Training & eLearning" desc="eLearning modules, compliance and certificates." canManage={emp.can_manage} allowUpload />
        <DocTab tab="performance" employee={emp} title="Performance documents" desc="Reviews, supervisions and audit scores." canManage={emp.can_manage} extra={<RecordsPanel employee={emp} canManage={emp.can_manage} />} />
        <DocTab tab="documents" employee={emp} title="Documents" desc="Created from templates or uploaded files." canManage={emp.can_manage} allowUpload />
        <DocTab tab="development" employee={emp} title="Development" desc="CPD logs, reflections and career goals." canManage={emp.can_manage} />
      </Tabs>

      <EmployeeForm open={editOpen} onOpenChange={setEditOpen} employee={emp} onSaved={load} />
    </div>
  );
}

function Detail({ label, value }) {
  return (
    <div>
      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-400">{label}</dt>
      <dd className="mt-0.5 text-sm font-medium text-slate-900">{value ?? "—"}</dd>
    </div>
  );
}

function DocTab({ tab, employee, title, desc, canManage, allowUpload, extra }) {
  const category = CAT_FOR_TAB[tab];
  const [docs, setDocs] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get("/documents", { params: { employee_id: employee.id, category } }).then((r) => setDocs(r.data));
  }, [employee.id, category]);
  useEffect(() => { load(); api.get("/templates", { params: { category } }).then((r) => setTemplates(r.data)); }, [load, category]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post("/documents/upload", fd, {
        params: { employee_id: employee.id, title: file.name, category },
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("File uploaded");
      load();
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const del = async (docId) => {
    await api.delete(`/documents/${docId}`);
    toast.success("Removed");
    load();
  };

  const download = (d) => {
    const token = localStorage.getItem("esp26_token");
    api.get(`/documents/${d.id}/download`, { responseType: "blob" }).then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = d.original_filename || d.title;
      a.click();
      URL.revokeObjectURL(url);
    });
  };

  return (
    <TabsContent value={tab} className="mt-5 space-y-6">
      {extra}
      <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h3 className="font-heading font-bold text-slate-900">{title}</h3>
            <p className="text-sm text-slate-500">{desc}</p>
          </div>
          {canManage && (
            <div className="flex items-center gap-2">
              <Button data-testid={`${tab}-new-document`} onClick={() => setCreateOpen(true)} variant="outline" size="sm">
                <Plus className="mr-1 h-4 w-4" /> New from template
              </Button>
              {allowUpload && (
                <label className="inline-flex cursor-pointer items-center gap-1 rounded-md bg-orange-500 px-3 py-1.5 text-sm font-medium text-white hover:bg-orange-600">
                  <Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload"}
                  <input data-testid={`${tab}-upload-input`} type="file" className="hidden" onChange={(e) => upload(e.target.files[0])} />
                </label>
              )}
            </div>
          )}
        </div>

        {docs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 py-10 text-center text-sm text-slate-400">
            No {title.toLowerCase()} records yet.
          </div>
        ) : (
          <ul className="space-y-2">
            {docs.map((d) => (
              <li key={d.id} data-testid="document-item" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
                <div className="flex items-center gap-3">
                  <FileText className="h-5 w-5 text-orange-500" />
                  <div>
                    <p className="text-sm font-medium text-slate-900">{d.title}</p>
                    <p className="text-xs text-slate-400">{d.doc_kind === "file" ? "Uploaded" : "Document"} · {shortDate(d.created_at)} · {d.created_by}</p>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {d.doc_kind === "file" && (
                    <button onClick={() => download(d)} className="rounded p-1.5 text-slate-500 hover:bg-slate-100"><Download className="h-4 w-4" /></button>
                  )}
                  {canManage && (
                    <button onClick={() => del(d.id)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <NewDocDialog open={createOpen} onOpenChange={setCreateOpen} employee={employee} category={category} templates={templates} onSaved={load} />
    </TabsContent>
  );
}

function NewDocDialog({ open, onOpenChange, employee, category, templates, onSaved }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [templateId, setTemplateId] = useState("");

  useEffect(() => { if (!open) { setTitle(""); setBody(""); setTemplateId(""); } }, [open]);

  const applyTemplate = (tid) => {
    setTemplateId(tid);
    const t = templates.find((x) => x.id === tid);
    if (t) { setBody(t.body); if (!title) setTitle(t.name); }
  };

  const save = async () => {
    if (!title) { toast.error("Title required"); return; }
    await api.post("/documents", { employee_id: employee.id, title, category, template_id: templateId || null, body });
    toast.success("Document created");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="new-document-dialog">
        <DialogHeader><DialogTitle className="font-heading">New document</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs uppercase text-slate-500">Template</Label>
            <Select value={templateId} onValueChange={applyTemplate}>
              <SelectTrigger className="mt-1.5" data-testid="template-select"><SelectValue placeholder="Start from a template (optional)" /></SelectTrigger>
              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Title</Label>
            <Input className="mt-1.5" data-testid="doc-title-input" value={title} onChange={(e) => setTitle(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs uppercase text-slate-500">Content</Label>
            <Textarea className="mt-1.5 min-h-[160px]" data-testid="doc-body-input" value={body} onChange={(e) => setBody(e.target.value)} />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="doc-save-button" onClick={save} className="bg-orange-500 hover:bg-orange-600">Create</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function SkillsMatrix({ employee, canManage }) {
  const [skills, setSkills] = useState([]);
  const load = useCallback(() => api.get(`/employees/${employee.id}/skills`).then((r) => setSkills(r.data)), [employee.id]);
  useEffect(() => { load(); }, [load]);

  const setLevel = async (skill_id, level) => {
    await api.put(`/employees/${employee.id}/skills`, { skill_id, level });
    setSkills((s) => s.map((x) => (x.skill_id === skill_id ? { ...x, level } : x)));
  };

  return (
    <div data-testid="skills-matrix" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <h3 className="mb-4 font-heading font-bold text-slate-900">Skills matrix</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {skills.map((s) => {
          const meta = LEVELS.find((l) => l.v === s.level) || LEVELS[0];
          return (
            <div key={s.skill_id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
              <div>
                <p className="text-sm font-medium text-slate-900">{s.name}</p>
                <p className="text-xs text-slate-400">{s.category}</p>
              </div>
              {canManage ? (
                <Select value={s.level} onValueChange={(v) => setLevel(s.skill_id, v)}>
                  <SelectTrigger data-testid="skill-level-select" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEVELS.map((l) => <SelectItem key={l.v} value={l.v}>{l.l}</SelectItem>)}</SelectContent>
                </Select>
              ) : (
                <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${meta.cls}`}>{meta.l}</span>
              )}
            </div>
          );
        })}
        {skills.length === 0 && <p className="text-sm text-slate-400">No skills defined. An admin can add skills in Administration.</p>}
      </div>
    </div>
  );
}

function RecordsPanel({ employee, canManage }) {
  const [records, setRecords] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => api.get("/records", { params: { employee_id: employee.id } }).then((r) => setRecords(r.data)), [employee.id]);
  useEffect(() => { load(); }, [load]);

  const complete = async (r) => {
    await api.put(`/records/${r.id}`, { status: r.status === "completed" ? "scheduled" : "completed" });
    load();
  };

  return (
    <div data-testid="records-panel" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h3 className="font-heading font-bold text-slate-900">Reviews & supervisions</h3>
          <p className="text-sm text-slate-500">Scheduled supervisions, reviews and assessments.</p>
        </div>
        {canManage && (
          <Button data-testid="add-employee-record" onClick={() => setOpen(true)} variant="outline" size="sm">
            <Plus className="mr-1 h-4 w-4" /> Schedule
          </Button>
        )}
      </div>
      {records.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No reviews scheduled yet.</div>
      ) : (
        <ul className="space-y-2">
          {records.map((r) => (
            <li key={r.id} data-testid="employee-record-item" className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-slate-900">{RECORD_LABEL[r.record_type] || r.record_type}</p>
                <p className="text-xs text-slate-400">{shortDate(r.scheduled_date)}</p>
              </div>
              <div className="flex items-center gap-2">
                {r.status === "completed" ? (
                  <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">Completed</span>
                ) : r.overdue ? (
                  <span className="rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700">Overdue</span>
                ) : (
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">Scheduled</span>
                )}
                {canManage && (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => complete(r)}>
                    {r.status === "completed" ? "Reopen" : "Complete"}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      <RecordDialog open={open} onOpenChange={setOpen} onSaved={load} presetEmployeeId={employee.id} />
    </div>
  );
}

const CONSENT_META = {
  given: { label: "Consent given", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  withdrawn: { label: "Consent withdrawn", cls: "bg-red-50 text-red-700 border-red-200" },
  not_recorded: { label: "Not recorded", cls: "bg-slate-100 text-slate-600 border-slate-200" },
};

function GdprPanel({ employee, user, onChanged }) {
  const [data, setData] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isAdmin = !!(user?.is_super || user?.role === "admin");

  const load = useCallback(
    () => api.get(`/employees/${employee.id}/gdpr`).then((r) => setData(r.data)).catch(() => setData(null)),
    [employee.id],
  );
  useEffect(() => { load(); }, [load]);
  if (!data) return null;

  const consent = data.consent || { status: "not_recorded" };
  const cm = CONSENT_META[consent.status] || CONSENT_META.not_recorded;

  const setConsent = async (status) => {
    await api.post(`/employees/${employee.id}/gdpr/consent`, { status });
    toast.success(`Consent ${status}`);
    load();
  };

  const exportData = () => {
    api.get(`/employees/${employee.id}/gdpr/export`, { responseType: "blob" }).then((res) => {
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `gdpr-export-${(employee.full_name || employee.id).replace(/\s+/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data export downloaded");
    }).catch(() => toast.error("Export failed"));
  };

  const anonymise = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/employees/${employee.id}/gdpr/anonymise`);
      toast.success(`Record anonymised · ${res.data.documents_erased} document(s) erased`);
      setConfirmOpen(false);
      onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not anonymise record");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="gdpr-panel" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <ShieldCheck className="h-5 w-5 text-orange-500" />
        <div>
          <h3 className="font-heading font-bold text-slate-900">Data &amp; privacy (GDPR)</h3>
          <p className="text-sm text-slate-500">Consent, data access export and right to erasure.</p>
        </div>
      </div>

      {/* Consent */}
      <div className="flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Consent to process data</p>
          <div className="mt-1.5 flex items-center gap-2">
            <span data-testid="consent-status-badge" className={`rounded-full border px-2 py-0.5 text-xs font-medium ${cm.cls}`}>{cm.label}</span>
            {consent.recorded_at && (
              <span className="text-xs text-slate-400">by {consent.recorded_by} · {shortDate(consent.recorded_at)}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="consent-given-button" onClick={() => setConsent("given")} variant="outline" size="sm" disabled={employee.gdpr_anonymised}>
            Record given
          </Button>
          <Button data-testid="consent-withdrawn-button" onClick={() => setConsent("withdrawn")} variant="outline" size="sm" disabled={employee.gdpr_anonymised}>
            Record withdrawn
          </Button>
        </div>
      </div>

      {/* Admin actions */}
      {isAdmin && (
        <div className="mt-3 flex flex-col gap-3 rounded-lg border border-slate-200 p-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Data subject rights</p>
            <p className="mt-1 text-sm text-slate-600">Export all held data, or anonymise this record (right to erasure).</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="gdpr-export-button" onClick={exportData} variant="outline" size="sm">
              <Download className="mr-1 h-4 w-4" /> Export data
            </Button>
            <Button
              data-testid="gdpr-anonymise-button"
              onClick={() => setConfirmOpen(true)}
              size="sm"
              disabled={employee.gdpr_anonymised}
              className="bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              <Lock className="mr-1 h-4 w-4" /> {employee.gdpr_anonymised ? "Anonymised" : "Anonymise"}
            </Button>
          </div>
        </div>
      )}

      {/* Audit log */}
      {data.audit?.length > 0 && (
        <div className="mt-4">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <History className="h-3.5 w-3.5" /> Recent GDPR activity
          </p>
          <ul className="space-y-1.5">
            {data.audit.slice(0, 5).map((a) => (
              <li key={a.id} data-testid="gdpr-audit-item" className="flex items-center justify-between rounded-md bg-slate-50 px-3 py-2 text-xs">
                <span className="font-medium capitalize text-slate-700">{a.action}{a.detail ? ` — ${a.detail}` : ""}</span>
                <span className="text-slate-400">{a.actor_name} · {shortDate(a.timestamp)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="max-w-md" data-testid="anonymise-confirm-dialog">
          <DialogHeader><DialogTitle className="font-heading">Anonymise this record?</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">
            This permanently removes personal identifiers (name, address, phone numbers, email) and erases all uploaded
            documents for <strong>{employee.full_name}</strong>. This action is logged and cannot be undone.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button data-testid="anonymise-confirm-button" onClick={anonymise} disabled={busy} className="bg-red-600 text-white hover:bg-red-700">
              {busy ? "Anonymising…" : "Yes, anonymise"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function OnboardingChecklist({ employee, canManage }) {
  const [data, setData] = useState(null);
  const load = useCallback(
    () => api.get(`/employees/${employee.id}/onboarding`).then((r) => setData(r.data)).catch(() => setData(null)),
    [employee.id],
  );
  useEffect(() => { load(); }, [load]);
  if (!data || !data.exists) return null;
  const items = data.items || [];
  const done = items.filter((i) => i.done).length;
  const pct = items.length ? Math.round((done / items.length) * 100) : 0;

  const toggle = async (key, value) => {
    setData((d) => ({ ...d, items: d.items.map((i) => (i.key === key ? { ...i, done: value } : i)) }));
    try { await api.put(`/employees/${employee.id}/onboarding`, { item_key: key, done: value }); }
    catch (e) { toast.error("Could not update checklist"); load(); }
  };

  return (
    <div data-testid="onboarding-checklist" className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-orange-500" />
          <div>
            <h3 className="font-heading font-bold text-slate-900">New starter onboarding</h3>
            <p className="text-sm text-slate-500">The trainer works through these to complete induction.</p>
          </div>
        </div>
        <span data-testid="onboarding-progress" className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">{done}/{items.length} done</span>
      </div>
      <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-orange-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <ul className="space-y-2">
        {items.map((i) => (
          <li key={i.key} className="flex items-center justify-between rounded-lg border border-slate-100 px-3 py-2">
            <button
              data-testid={`onboarding-item-${i.key}`}
              onClick={() => canManage && toggle(i.key, !i.done)}
              disabled={!canManage}
              className={`flex items-center gap-2.5 text-left text-sm ${canManage ? "cursor-pointer" : "cursor-default"}`}
            >
              {i.done ? <CheckSquare className="h-5 w-5 shrink-0 text-emerald-500" /> : <Square className="h-5 w-5 shrink-0 text-slate-300" />}
              <span className={i.done ? "text-slate-400 line-through" : "text-slate-700"}>{i.label}</span>
              <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-500">{i.category}</span>
            </button>
            {i.done && i.done_by && <span className="text-xs text-slate-400">{i.done_by}</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

